import { type FormEvent, useId } from "react";
import { Icon, type IconName } from "../Icon";
import { RequiredMarker } from "../RequiredMarker";

/**
 * The white bar on the questionnaire and on every secure-link page: Bob Doc, the clinic once it
 * is known, a lock label, and End session while there is anything to end.
 */
export function PatientTopBar({
  label,
  clinicName,
  onEndSession,
}: {
  /** The lock label, such as "Secure questionnaire". Hidden on phones. */
  readonly label: string;
  readonly clinicName?: string | undefined;
  readonly onEndSession?: (() => void) | undefined;
}) {
  return (
    <header className="topbar">
      <div className="patient-brand">
        <div>
          <img className="patient-bob-wordmark" src="/brand/bob/wordmark.svg" alt="Bob Doc" />
          {clinicName === undefined ? null : <small>for {clinicName}</small>}
        </div>
      </div>
      <div className="topbar-actions">
        <span className="secure-label">
          <Icon name="lock" />
          {label}
        </span>
        {onEndSession === undefined ? null : (
          <button type="button" className="end-session-button" onClick={onEndSession}>
            End session
          </button>
        )}
      </div>
    </header>
  );
}

/**
 * The round mark above a state card's heading. Decorative: the heading says what happened.
 * info: the pale wash (a lock, a document); done: filled, for something that worked; problem:
 * amber.
 */
export function StateMark({
  icon,
  tone = "info",
}: {
  readonly icon: IconName;
  readonly tone?: "info" | "done" | "problem";
}) {
  return (
    <span className={`state-mark ${tone}`} aria-hidden="true">
      <Icon name={icon} />
    </span>
  );
}

/**
 * The date of birth that opens a secure link (a questionnaire, an appointment or a document).
 * Nothing about the link is shown before it. A refusal shows between the field and the button
 * and describes the field. It does not mark the field invalid: the service answers a wrong date
 * of birth and an expired link the same way, and a lost connection is not the field's fault.
 */
export function DateOfBirthForm({
  value,
  onChange,
  onSubmit,
  busy,
  error,
}: {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly onSubmit: (event: FormEvent) => void;
  readonly busy: boolean;
  /** Why the link did not open, as patient-safe text. */
  readonly error?: string | null | undefined;
}) {
  const id = `dob-${useId().replaceAll(":", "")}`;
  const errorId = `${id}-error`;
  const shownError = error ? error : null;
  return (
    <form className="secure-link-form" onSubmit={onSubmit}>
      <div className="field">
        <label htmlFor={id}>
          Date of birth
          <RequiredMarker />
        </label>
        <input
          id={id}
          type="date"
          value={value}
          required
          autoComplete="off"
          {...(shownError === null ? {} : { "aria-describedby": errorId })}
          onChange={(event) => onChange(event.target.value)}
        />
      </div>
      {shownError === null ? null : (
        <p id={errorId} className="secure-link-error" role="alert">
          {shownError}
        </p>
      )}
      <button type="submit" className="continue-button" disabled={busy}>
        {busy ? (
          <>
            <span className="button-spinner" />
            Opening…
          </>
        ) : (
          "Open secure link"
        )}
      </button>
    </form>
  );
}
